import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import Fireworks from 'react-canvas-confetti/dist/presets/fireworks';
import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDidMount } from 'rooks';
import { setupNotifierForAnnotation } from '@/annotation/annotation.actions';
import { TabTitle } from '@/core/TabTitle.molecule';
import { sqStateSynchronizer, sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { isSystemTest } from '@/core/utilities';
import { Footer } from '@/footer/Footer.molecule';
import { Header } from '@/header/Header.atom';
import { loadHomeScreenAddOnTools } from '@/homescreen/addOnTool.actions';
import { useAddOnToolActions } from '@/homescreen/addOnTool.store';
import { LicenseExpirationWarning } from '@/licenseManagement/LicenseExpirationWarning';
import { useDisplayLicenseWarning } from '@/licenseManagement/licenseManagement.store';
import { useLicenseStatusCheck } from '@/licenseManagement/useLicenseStatusCheck.hook';
import { OldLoadingBar } from '@/main/OldLoadingBar.molecule';
import { ResizableWindowWrapper } from '@/main/ResizableWindowWrapper';
import { cleanupOnUnload } from '@/main/cleanup.utilities';
import {
  goTo,
  isAIStandalone,
  isEmbeddedView,
  isMarkdownFileViewer,
  isWorksheet,
  navigateToCorrectErrorPage,
  setCurrentPath,
  wasWorksheet,
} from '@/main/routing.utilities';
import { PasswordWarning } from '@/passwordWarning/passwordWarning.organism';
import { load as loadPlugins } from '@/plugin/plugin.actions';
import { cancelAll, forEachPending } from '@/requests/pendingRequests.utilities';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { onAllServerRequestsCanceled } from '@/services/notifier.service';
import { fetchEveryoneDisabled, newHeaderExperienceEnabled } from '@/services/systemConfiguration.utilities';
import { SystemWarning } from '@/systemWarning/SystemWarning.organism';
import { WorkbenchTourModal } from '@/tour/WorkbenchTourModal.organism';
import { cleanup } from '@/trendData/duration.actions';
import { getCsrfToken } from '@/utilities/auth.utilities';
import { switchLanguage } from '@/utilities/i18n.utilities';
import { initializeRedactionService } from '@/utilities/redaction.utilities';
import { fetchHeadlessCaptureMetadata } from '@/utilities/screenshot.utilities';
import { open as openSocket } from '@/utilities/socket.utilities';
import { successToast, warnToast } from '@/utilities/toast.utilities';
import { switchModeAndTheme } from '@/utilities/utilities';
import { isEditWorkbookMode, isPresentationWorkbookMode } from '@/utilities/workbookMode.utilities';
import { VantageConditionMonitorStoppedWarning } from '@/vantage/VantageConditionMonitorStoppedWarning';
import { useStoppedConditionMonitorState, useVantageWorkbookActions } from '@/vantage/vantageWorkbook.store';
import { setCurrentUser, setDarkMode, setStateParams, setTriggerConfetti } from '@/workbench/workbench.actions';
import { WORKBENCH_SCHEMA_VERSION } from '@/workbench/workbench.constants';
import { getPageTheme, getWorkbench } from '@/workbench/workbench.utilities';
import { applyWorkbenchConfigUpgrade } from '@/workbench/workbenchUpgrader.utilities';
import { setupNotifierForWorkbook } from '@/workbook/workbook.actions';
import type { TConductorInstance } from 'react-canvas-confetti/dist/types';

/**
 * Wrapper component contains all the components that need to access workbench state.
 * This component takes care of subscribing to websocket channels, setting up state in the stores ...
 */
export const WorkbenchWrapperPage = () => {
  let confettiTimeOutOne: ReturnType<typeof setTimeout>;
  let confettiTimeOutTwo: ReturnType<typeof setTimeout>;
  let confettiTimeOutThree: ReturnType<typeof setTimeout>;

  const [dataLoaded, setDataLoaded] = useState(false);

  const { pathname } = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const urlSearchParams = useMemo(() => new URLSearchParams(searchParams), [searchParams]);
  const searchParamsAsObject = useMemo(() => Object.fromEntries(urlSearchParams), [urlSearchParams]);

  const notDisplayingPresentationMode = !_.includes(pathname, 'present/');
  const { setHomeScreenAddOnTools } = useAddOnToolActions();

  useLicenseStatusCheck();
  const displayLicenseWarning = useDisplayLicenseWarning();
  const stoppedConditionMonitorState = useStoppedConditionMonitorState();
  const { setWorkbook } = useVantageWorkbookActions();

  const conductorRef = useRef<TConductorInstance>(undefined);
  const onConfettiInit = ({ conductor }: { conductor: TConductorInstance }) => {
    conductorRef.current = conductor;
  };

  useEffect(
    () => () => {
      clearTimeout(confettiTimeOutOne);
      clearTimeout(confettiTimeOutTwo);
      clearTimeout(confettiTimeOutThree);
    },
    //useDidMount resulted in odd behavior in system test
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { triggerConfetti, confettiMessageKey, darkMode, interactiveSessionId, userLanguage } = useFluxPaths(
    sqWorkbenchStore,
    ['triggerConfetti', 'confettiMessageKey', 'darkMode', 'interactiveSessionId', 'userLanguage'],
  );
  if (triggerConfetti && !isSystemTest() && !headlessRenderMode()) {
    const conductor = conductorRef.current;
    conductor?.shoot();
    confettiTimeOutOne = setTimeout(() => conductor?.shoot(), 1000);
    confettiTimeOutTwo = setTimeout(() => conductor?.shoot(), 1500);
    confettiTimeOutThree = setTimeout(() => conductor?.shoot(), 2000);
    if (confettiMessageKey) {
      successToast({ messageKey: confettiMessageKey });
    }
    setTriggerConfetti(false, '');
  }

  const { isVantage, isAnalysis, isTopic, isActionBoard } = useFluxPaths(sqWorkbookStore, [
    'isVantage',
    'isAnalysis',
    'isTopic',
    'isActionBoard',
  ]);

  useEffect(() => {
    switchModeAndTheme(darkMode && !headlessRenderMode(), getPageTheme(isVantage, isAnalysis, isTopic, isActionBoard));
  }, [isVantage, isAnalysis, isTopic, isActionBoard, darkMode, pathname]);

  useEffect(() => {
    // The order of operations and the actions taken are important for preventing worksteps leaking!
    setCurrentPath(pathname);
    // Dismiss toasts when transitioning from homescreen to worksheet or vice versa
    if (wasWorksheet() !== isWorksheet()) {
      toast.dismiss();
    }

    const newStateParams = { ...params, ...searchParamsAsObject };

    // If we are already transitioning, it is unsafe to start another transition without canceling or
    // otherwise letting the in progress transition finish normally. It isn't safe because loading most
    // states requires asynchronous requests that change global state (the stores), if two transitions are
    // running at the same time then that leads to race conditions concerning the final global state. The
    // only way to effectively cancel the transition is to cause the page to reload. This isn't ideal for
    // the user experience, but it is better than letting state from two worksheets mingle (CRAB-35397).
    if (
      wasWorksheet() &&
      sqStateSynchronizer.unsafeTransition() &&
      sqWorkbenchStore.stateParams.worksheetId !== newStateParams.worksheetId
    ) {
      sqStateSynchronizer
        .checkNavigationIsSafe()
        .then(([finished, pendingRequestsCount, stillHydrating, shouldNavigate]) => {
          const url = _.join(_.compact([pathname, urlSearchParams.toString()]), '?');
          if (!shouldNavigate) {
            console.info(`Stopping navigation to ${url} because the URL changed by the user while rehydrating`);
          } else if (finished) {
            // We can use our router to navigate to the new URL
            goTo(url, true);
          } else {
            // Well, we tried, but it's not safe to navigate yet. We give up and reload the page to prevent state leaks.
            console.info(`Unsafe navigation ${{ pendingRequestsCount, stillHydrating }}. Reloading the page.`);
            forEachPending((id, canceler) => console.info(`Pending request: ${id}`, canceler.config));
            window.location.assign(url);
          }
        });

      return;
    }

    // Prevents reloading when the user double clicks
    if (!_.isEqual(sqWorkbenchStore.stateParams, newStateParams)) {
      if (wasWorksheet()) {
        // The pendingRequestsCount() should ensure that if any requests remain the page is reloaded, but just
        // in case something was not picked up we cancel all outstanding requests that were initiated on the worksheet
        cancelAll().finally(() => {
          // Ensures all websocket subscriptions are closed
          cleanupOnUnload();
          cleanup();
          setStateParams(newStateParams);
        });
      } else {
        setStateParams(newStateParams);
      }
    }
  }, [pathname, params, searchParams, searchParamsAsObject, urlSearchParams]);

  useEffect(() => {
    switchLanguage(userLanguage);
  }, [userLanguage]);

  useDidMount(() => {
    // set up notifiers
    setupNotifierForWorkbook(setWorkbook);
    setupNotifierForAnnotation();

    // Notify the user if an admin user cancels all server requests
    onAllServerRequestsCanceled(() => {
      warnToast({ messageKey: 'REQUEST_CANCELLATION.ALL_BY_ADMIN' });
    });

    initializeRedactionService();

    Promise.all([
      // Fetch initial data
      fetchEveryoneDisabled(),
      // setCurrentUser() must be before loading the plugins so admins only all in-development Add-ons
      setCurrentUser()
        .then(() => loadPlugins())
        .then(() => loadHomeScreenAddOnTools())
        .then((addOnTools) => setHomeScreenAddOnTools(addOnTools))
        .catch(_.noop),
      // Miscellaneous setup
      fetchHeadlessCaptureMetadata(),
      openSocket(interactiveSessionId, getCsrfToken()),
    ])
      .then(() => {
        const workbenchState = getWorkbench();
        const upgradedState = applyWorkbenchConfigUpgrade(
          workbenchState,
          workbenchState?.stores?.sqWorkbenchStore?.version || 1,
          WORKBENCH_SCHEMA_VERSION,
        );

        sqStateSynchronizer.rehydrate(upgradedState, {
          persistenceLevel: 'WORKBENCH',
          initializeMode: 'SOFT',
        });
      })
      .then(() => {
        const darkModeParam = searchParamsAsObject['darkMode'];
        if (darkModeParam) {
          setDarkMode(darkModeParam === 'enabled' || darkModeParam === 'true');
        }
      })
      .then(() => {
        setDataLoaded(true);
      })
      .catch((error) => {
        navigateToCorrectErrorPage(error);
      });
  });

  const isEmbedded = isEmbeddedView();
  const aiStandalone = isAIStandalone();
  const markdownFileViewer = isMarkdownFileViewer();
  const isInAIFrame = typeof window !== 'undefined' && window.self !== window.top && _.includes(pathname, '/ai');

  return (
    <>
      <TabTitle />

      {dataLoaded ? (
        <div className={clsx('flexRowContainer', 'fullViewport', {})} id="mainView" data-testid="mainView">
          {!isVantage && !newHeaderExperienceEnabled() && !headlessRenderMode() && !isPresentationWorkbookMode() ? (
            <OldLoadingBar />
          ) : null}
          {notDisplayingPresentationMode && !isInAIFrame && (
            <>
              {displayLicenseWarning && <LicenseExpirationWarning />}
              <SystemWarning />
              <PasswordWarning />
              {isAnalysis && isEditWorkbookMode() && <WorkbenchTourModal />}
              {isVantage && stoppedConditionMonitorState && (
                <VantageConditionMonitorStoppedWarning stoppedConditionMonitorState={stoppedConditionMonitorState} />
              )}
            </>
          )}
          {notDisplayingPresentationMode && !aiStandalone && !markdownFileViewer ? <Header /> : null}
          <div className="flexFill flexColumnContainer">
            <ResizableWindowWrapper>
              <Outlet />
            </ResizableWindowWrapper>
          </div>
          {notDisplayingPresentationMode && !aiStandalone && !isEmbedded ? <Footer /> : null}
        </div>
      ) : null}
      <div className="fireworks">
        {!isSystemTest() && !headlessRenderMode() && <Fireworks onInit={onConfettiInit} />}
      </div>
    </>
  );
};
